import { ReactCompareSlider } from "react-compare-slider";

interface BeforeAfterSliderProps {
  beforeImage: string;
  afterImage: string;
  /**
   * When the after image may contain transparency (e.g. background removed),
   * render both sides on a light backdrop instead of the default dark one.
   */
  transparentAfter?: boolean;
}

function SliderItem({
  src,
  alt,
  bg,
}: {
  src: string;
  alt: string;
  bg: string;
}) {
  return (
    <div
      style={{
        // Fill the clip container completely so this layer is always opaque
        // across its whole region. Otherwise the layer underneath (the "before"
        // image) can show through the letterboxed areas of the "after" image.
        position: "absolute",
        inset: 0,
        background: bg,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <img
        src={src}
        alt={alt}
        draggable={false}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "contain",
          display: "block",
        }}
      />
    </div>
  );
}

export function BeforeAfterSlider({
  beforeImage,
  afterImage,
  transparentAfter = false,
}: BeforeAfterSliderProps) {
  // Use a single shared backdrop for both sides. When before/after images have
  // different aspect ratios their letterboxed (object-fit: contain) areas line
  // up against the same color, so neither image's background appears to overlay
  // the other when the slider is dragged.
  const bg = transparentAfter ? "#f5f5f5" : "#111";

  return (
    <div
      className="relative w-full h-full rounded-xl overflow-hidden"
      style={{ background: bg }}
    >
      <ReactCompareSlider
        itemOne={<SliderItem src={beforeImage} alt="Before" bg={bg} />}
        itemTwo={<SliderItem src={afterImage} alt="After" bg={bg} />}
        position={50}
        className="w-full h-full"
        style={{ height: "100%" }}
      />
      <div className="absolute top-4 left-4 bg-black/60 text-white px-3 py-1.5 rounded-lg text-sm font-medium backdrop-blur-sm z-10 pointer-events-none">
        Before
      </div>
      <div className="absolute top-4 right-4 bg-black/60 text-white px-3 py-1.5 rounded-lg text-sm font-medium backdrop-blur-sm z-10 pointer-events-none">
        After
      </div>
    </div>
  );
}
